import isEmpty from 'lodash/isEmpty';
import {
  UPDATE_E_GIFT_CARD_USER_DISABLED,
  UPDATE_E_GIFT_CARD_RETAILER,
  UPDATE_E_GIFT_CARD_VALUE,
  STORE_E_GIFT_CARDS,
  TOGGLE_E_GIFT_CARDS_DRAWER,
  LOAD_SELECTED_E_GIFT_CARD,
  INIT_PRODUCT_CONFIG_ID,
} from './constants';
import {GIFT_CARD_DEFAULT_VALUE} from '~/CreateFabricGreetingCard/utils/constants';

const systemDisabled = !evite.features.isEnabled('bhn_enable_new', false);

/**
 * Returns the default state object.
 *
 * @param {boolean} [userDisabled=false] - Indicates whether the user is disabled.
 * @returns {Object} The default state object, containing properties such as `retailer`,
 * `productConfigId`, `productConfigName`, `image`, `value`, `systemDisabled`, `userDisabled`,
 * `isDrawerOpen`, and `products`.
 *
 */
const getDefaultState = (userDisabled = false) => ({
  retailer: null,
  productConfigId: window?.evite_event?.gift_card_data?.product_config_id || '',
  productConfigName: window?.evite_event?.gift_card_data?.product_config_name || '',
  image: window?.evite_event?.gift_card_data?.image || '',
  value: userDisabled
    ? 0
    : Math.round(window?.evite_event?.gift_card_data?.amount_in_cents) || GIFT_CARD_DEFAULT_VALUE,
  systemDisabled,
  userDisabled,
  isDrawerOpen: true,
  products: undefined,
});

/**
 * Returns the product with the matching event's selected `product_config_id` from
 * the `products` array, or the product matching the URL param `configId`, or
 * finally returns the first product as a fallback.
 *
 * @param {Object[]} products - The array of product objects.
 * @returns {Object} The product with the matching `product_config_id`, or the
 * product matching the URL param `configId`, or lastly the first product in the array.
 *
 * @example
 *
 * const products = [
 *   { productConfigId: 1, productConfigName: 'Product 1' },
 *   { productConfigId: 2, productConfigName: 'Product 2' },
 *   { productConfigId: 3, productConfigName: 'Product 3' },
 * ];
 *
 * // Assume window.evite_event.gift_card_data.product_config_id is 2
 * getRetailer(products);
 * // Returns { productConfigId: 2, productConfigName: 'Product 2' }
 */
const getRetailer = (state, products = []) => {
  const params = new URL(document.location.href).searchParams;
  const configId = params.get('product_config_id');
  const defaultRetailer = products.find((p) => p.product_config_id === configId);
  const eventConfigId =
    state.productConfigId ?? window?.evite_event?.gift_card_data?.product_config_id;
  return products.find((product) => product.product_config_id === eventConfigId) || defaultRetailer;
};

const initialState = getDefaultState(true);

export const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case INIT_PRODUCT_CONFIG_ID: {
      const retailer = getRetailer(state, state.products);
      return {
        ...state,
        productConfigId: action.payload,
        retailer,
        userDisabled: false,
      };
    }
    case LOAD_SELECTED_E_GIFT_CARD:
      if (!action.payload) return state;
      return {
        ...state,
        retailer: getRetailer(state, state.products) ?? action.payload,
        productConfigId: action.payload.product_config_id,
        productConfigName: action.payload.product_config_name,
        image: action.payload.image,
        value: action.payload.amount_in_cents,
        userDisabled: !action.payload.product_config_id,
        isDrawerOpen: !action.payload.product_config_id,
      };
    case UPDATE_E_GIFT_CARD_RETAILER: {
      let {value} = action.payload;
      if (!value) {
        if (action.payload.product_config_id) {
          value = GIFT_CARD_DEFAULT_VALUE;
        } else {
          value = 0;
        }
      }
      return {
        ...state,
        retailer: action.payload,
        productConfigId: action.payload.product_config_id,
        productConfigName: action.payload.product_config_name,
        image: action.payload.image,
        value,
        userDisabled: !action.payload.product_config_id,
      };
    }
    case UPDATE_E_GIFT_CARD_VALUE:
      return {...state, value: action.payload};
    case UPDATE_E_GIFT_CARD_USER_DISABLED:
      return {
        ...state,
        userDisabled: action.payload,
        retailer: action.payload ? state?.products?.[0] : state.retailer,
        isDrawerOpen: !action.payload,
      };
    case STORE_E_GIFT_CARDS: {
      if (isEmpty(action.payload)) {
        return {
          ...state,
          userDisabled: true,
          systemDisabled: true,
        };
      }
      const initialRetailer = getRetailer(state, action.payload);
      return {
        ...state,
        systemDisabled,
        products: action.payload,
        retailer: state.userDisabled ? null : initialRetailer,
        productConfigName: state.userDisabled ? '' : initialRetailer?.product_config_name,
        productConfigId: state.userDisabled ? '' : initialRetailer?.product_config_id,
        image: state.userDisabled ? '' : initialRetailer?.image,
        isDrawerOpen: !initialRetailer,
      };
    }
    case TOGGLE_E_GIFT_CARDS_DRAWER:
      return {...state, isDrawerOpen: action.payload};
    default:
      return state;
  }
};
